import type { AvaAIUITools } from '@talos/shared/avaAI';
import type { ChatStatus, UIMessage } from 'ai';
import { createContext, useContext } from 'react';

export type AvaAIStatus = ChatStatus;
export type { AvaAIUITools };
export type AvaAIUIMessage = UIMessage<never, never, AvaAIUITools>;

export interface AvaAIVisibilityContextProps {
  isEnabled: boolean;
  isOpen: boolean;
  isPopped: boolean;
  open: () => void;
  close: () => void;
  popout: () => void;
  dockBack: () => void;
  /** Remember the open popout so later actions can focus it without docking. */
  registerPopoutWindow: (popoutWindow: Window | null) => void;
  /** Bring the popped-out chat window to the front. */
  focusPopout: () => void;
  /** Focus the chat input, including after a closing modal restores the previous focus. */
  focusComposer: () => void;
  /** Open the docked drawer or focus the popout, then focus the composer. */
  openAndFocusComposer: () => void;
}

export interface AvaAIContextProps extends AvaAIVisibilityContextProps {
  messages: AvaAIUIMessage[];
  status: AvaAIStatus;
  error: Error | undefined;
  composerDraft: string;
  setComposerDraft: (draft: string) => void;
  reset: () => void;
  sendMessage: (text: string) => Promise<void>;
  sendPowerSearchMessage: (text: string) => Promise<void>;
  cancelPowerSearchTurn: () => void;
  /** Drop the active turn when ephemeral UI unmounts, unless the user already continued in chat. */
  abandonTurn: () => void;
  /** Keep the active power-search turn when its result row opens the full chat. */
  preserveActiveTurn: () => void;
  /** User message ID for the active power-search turn, if any. */
  powerSearchMessageId: string | null;
  regenerate: () => Promise<void>;
  stop: () => Promise<void>;
  clearError: () => void;
  composerFocusRequest: number;
}

export const AvaAIVisibilityContext = createContext<AvaAIVisibilityContextProps | undefined>(undefined);
AvaAIVisibilityContext.displayName = 'AvaAIVisibilityContext';

export const AvaAIContext = createContext<AvaAIContextProps | undefined>(undefined);
AvaAIContext.displayName = 'AvaAIContext';

export function useAvaAIVisibility(): AvaAIVisibilityContextProps {
  const context = useContext(AvaAIVisibilityContext);
  if (context === undefined) {
    throw new Error('Missing AvaAIVisibilityContext.Provider further up in the tree. Did you forget to add it?');
  }
  return context;
}

export function useAvaAI(): AvaAIContextProps {
  const context = useContext(AvaAIContext);
  if (context === undefined) {
    throw new Error('Missing AvaAIContext.Provider further up in the tree. Did you forget to add it?');
  }
  return context;
}
